import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { BookUser } from 'lucide-react';
import { useFetchContact, useUpdateContact } from '@/hooksApi/useContact';
import ContactForm, { buildPayload, prefillFromApi } from './ContactForm';
import type { ContactFormState } from './ContactForm';

const SettingsAddressBookEdit = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [prefilled, setPrefilled] = useState<ContactFormState | undefined>(undefined);

  const { data: singleContact, isFetching, isError } = useFetchContact(id ?? '');

  const { mutate: updateContact, isLoading: isUpdating } = useUpdateContact(id ?? '');

  /* Prefill form once data arrives */
  useEffect(() => {
    if (singleContact?.object) {
      setPrefilled(prefillFromApi(singleContact.object));
    }
  }, [singleContact]);

  const handleSave = (form: ContactFormState) => {
    if (!id) return;
    const payload = buildPayload(form);
    updateContact(payload, {
      onSuccess: () => {
        toast({ title: t('success_title'), description: t('contact_updated') });
        queryClient.invalidateQueries(['contacts']);
        navigate('/settings/address-book');
      },
      onError: (error: any) => {
        const msg = error?.response?.data?.message || error?.message || t('operation_failed');
        toast({ title: t('error_title'), description: msg, variant: 'destructive' });
      },
    });
  };

  /* Loading state — skeleton matching form layout */
  if (isFetching && !prefilled) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 max-w-3xl w-full mx-auto'>
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-6'>
            <div className='animate-pulse space-y-6'>
              <div className='h-5 bg-sd-muted rounded w-1/3' />
              <div className='space-y-4'>
                <div className='h-10 bg-sd-muted rounded-lg' />
                <div className='h-10 bg-sd-muted rounded-lg' />
                <div className='grid grid-cols-2 gap-4'>
                  <div className='h-10 bg-sd-muted rounded-lg' />
                  <div className='h-10 bg-sd-muted rounded-lg' />
                </div>
                <div className='h-10 bg-sd-muted rounded-lg' />
                <div className='h-[280px] bg-sd-muted rounded-lg' />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* Error / not found state */
  if (isError || (!isFetching && !singleContact?.object)) {
    return (
      <div className='sd-scope sd-font flex flex-col'>
        <div className='p-6 max-w-3xl w-full mx-auto'>
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-10 flex flex-col items-center text-center gap-3'>
            <div className='h-12 w-12 rounded-xl bg-sd-muted flex items-center justify-center'>
              <BookUser className='h-6 w-6 text-sd-muted-foreground' />
            </div>
            <div className='space-y-1'>
              <p className='text-sm font-medium text-sd-foreground'>
                {String(t('contact_not_found'))}
              </p>
              <p className='text-xs text-sd-muted-foreground'>
                {String(t('contact_not_found_description'))}
              </p>
            </div>
            <button
              onClick={() => navigate('/settings/address-book')}
              className='mt-1 px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {String(t('back_to_address_book'))}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ContactForm
      heading={t('edit_contact')}
      initialForm={prefilled}
      isSaving={isUpdating}
      onSave={handleSave}
      isEditMode={true}
    />
  );
};

export default SettingsAddressBookEdit;
